/**
 * This library is the only place where you'd use raw AnalyticsContext
 * It provides a typed wrapper around it for better user experience
 */
// eslint-disable-next-line jira/filename-case
import React, { type ReactNode, memo } from 'react';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import type { Screens } from '../constants.tsx';
/**
 * Those rules are not smart enough to detect the use case here
 */
export type Container = {
	id: string;
	type?: string;
};
export type AnalyticsContextData = {
	readonly containerId?: string;
	readonly containerType?: string;
	readonly objectType?: string;
	readonly objectId?: string;
	readonly containers?: {
		[containerName: string]: Container;
	};
	readonly tags?: string[];
	readonly sourceName?: string;
	readonly sourceType?: Screens;
	readonly attributes?: {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
	};
	readonly children: ReactNode;
};

export const extractContextualData = (
	props: AnalyticsContextData,
): Record<
	string,
	| string
	| string[]
	| {
			[containerName: string]: Container;
	  }
	| {
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			[key: string]: any;
	  }
	| undefined
> => {
	const { children, sourceName, sourceType, ...rest } = props;
	const source = sourceName && sourceType ? `${sourceName}${sourceType}` : undefined;

	return omitBy(
		{
			source,
			...rest,
		},
		isUndefined,
	);
};

export const ContextualAnalyticsData: React.NamedExoticComponent<AnalyticsContextData> =
	memo<AnalyticsContextData>((props: AnalyticsContextData) => {
		const data = extractContextualData(props);

		return (
			<AnalyticsContext data={data}>
				<>{props.children}</>
			</AnalyticsContext>
		);
	});
